import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { countryName } from "@/modules/dashboard/cards/constants/countries.constants";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import { defaultExampleAmount } from "../constants/paymentFees.constants";
import { PaymentFeesService } from "../services/paymentFees.service";
import {
  PaymentDirection,
  PaymentFeeKind,
  type CorridorCoverage,
  type CorridorFilter,
  type FeeFiltersState,
  type FeeRuleGroup,
  type FeeTabId,
  type PaymentFee,
  type PaymentFeeRail,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import type { PaymentTax } from "../types/paymentTaxes.types";
import { corridorFilterKey } from "../utils/feeFormat";
import { buildCorridorCoverage, feeScopeOf, groupFeesByScope, scopeKey } from "../utils/feeMath";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXAMPLE_AMOUNT_PATTERN = /^\d{0,12}(\.\d{0,8})?$/;
const TAB_IDS: FeeTabId[] = ["corridors", "history", "commissions", "costs"];
/** Default page budget (10 × 200 rows) and the raised one behind the "load all" disclosure CTA. */
const FEES_DEFAULT_MAX_PAGES = 10;
const FEES_EXPANDED_MAX_PAGES = 50;

export interface CorridorKpis {
  total: number;
  priced: number;
  unpriced: number;
  special: number;
  negative: number;
  scheduled: number;
}

export interface UsePaymentFeesResult {
  /** All non-deleted rows currently loaded. The full set — coverage depends on it. */
  fees: PaymentFee[];
  /** Frozen per load, so every derived status agrees within one render pass. */
  now: number;
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 on the fee list latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  /** More rows exist than were loaded — must be disclosed, never silently capped. */
  truncated: boolean;
  loadedCount: number;
  totalCount: number;
  refresh: () => Promise<void>;
  loadAll: () => void;

  tab: FeeTabId;
  setTab: (tab: FeeTabId) => void;

  exampleAmounts: Record<string, string>;
  exampleAmountFor: (currency: string | null | undefined) => string;
  setExampleAmount: (currency: string, value: string) => void;
  resetExampleAmount: (currency: string) => void;

  filters: FeeFiltersState;
  setCorridor: (value: string) => void;
  setDirection: (value: PaymentDirection | "") => void;
  /** The organization the page is scoped to — `null` is the general price. */
  scopeOrganizationId: string | null;
  setScopeOrganizationId: (value: string | null) => void;
  setProviderCode: (value: string) => void;
  setShowSuperseded: (value: boolean) => void;
  setShowScheduled: (value: boolean) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  focusCorridor: (route: ProviderRailRoute, tab: FeeTabId) => void;

  corridors: CorridorCoverage[];
  visibleCorridors: CorridorCoverage[];
  corridorFilter: CorridorFilter;
  setCorridorFilter: (filter: CorridorFilter) => void;
  corridorKpis: CorridorKpis;
  /** General scope: organizations that pay their own price on at least one corridor. */
  organizationsWithSpecialPrice: string[];

  /** Groups of the active rule tab, already filtered and ordered most-specific-first. */
  groups: FeeRuleGroup[];
  expandedKeys: Record<string, boolean>;
  toggleGroup: (key: string) => void;
  highlightedKey: string | null;
  highlightGroup: (key: string) => void;
}

function isNegative(margin: string | null): boolean {
  return margin != null && compareDecimalStrings(margin, "0") < 0;
}

function matchesFilter(corridor: CorridorCoverage, filter: CorridorFilter, scoped: boolean): boolean {
  switch (filter) {
    case "unpriced":
      return corridor.status !== "PRICED";
    case "special":
      return scoped ? corridor.commissionSource === "ORGANIZATION" : corridor.orgOverrides.length > 0;
    case "negative":
      return isNegative(corridor.margin);
    case "scheduled":
      return corridor.hasScheduledChange;
    default:
      return true;
  }
}

/**
 * State and derived views of the price book. No polling: prices change by human action only, so data reloads
 * on the manual button and after every write. Tab, scope and rule filters live in the URL so a view is
 * shareable.
 */
const NO_TAXES: PaymentTax[] = [];

export function usePaymentFees(
  routes: ProviderRailRoute[],
  taxRows: PaymentTax[] = NO_TAXES,
): UsePaymentFeesResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const [fees, setFees] = useState<PaymentFee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const [maxPages, setMaxPages] = useState(FEES_DEFAULT_MAX_PAGES);
  const isLoading = settledKey !== reloadKey && !hasLoadedOnce;

  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [showScheduled, setShowScheduled] = useState(true);
  const [corridorFilter, setCorridorFilter] = useState<CorridorFilter>("all");
  const [exampleAmounts, setExampleAmounts] = useState<Record<string, string>>({});

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    PaymentFeesService.listAll({}, maxPages)
      .then((result) => {
        if (cancelled) return;
        setFees(result.rows.filter((row) => !row.deleted_at));
        setTotalCount(result.total);
        setTruncated(result.truncated);
        setHasError(false);
        setHasLoadedOnce(true);
        setNow(Date.now());
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(reloadKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, maxPages]);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  // Dropping `hasLoadedOnce` brings the skeletons back: figures derived from the partial set must not sit on
  // screen looking current while up to 10k rows load.
  const loadAll = useCallback(() => {
    setIsRefreshing(true);
    setHasLoadedOnce(false);
    setMaxPages(FEES_EXPANDED_MAX_PAGES);
    setReloadKey((key) => key + 1);
  }, []);

  /* ------------------------------- URL-backed state ------------------------------- */

  const rawTab = searchParams.get("tab");
  const tab: FeeTabId = TAB_IDS.includes(rawTab as FeeTabId) ? (rawTab as FeeTabId) : "corridors";

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      // react-router replaces the entry even for an identical URL, and WebKit throttles replaceState.
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value == null || value === "") next.delete(key);
        else next.set(key, value);
      }
      if (next.toString() === searchParams.toString()) return;

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const rawOrg = searchParams.get("organization_id");
  const rawDirection = searchParams.get("direction");
  const filters = useMemo<FeeFiltersState>(
    () => ({
      corridor: searchParams.get("corridor") ?? "",
      direction:
        rawDirection === PaymentDirection.PAYIN || rawDirection === PaymentDirection.PAYOUT
          ? rawDirection
          : "",
      organizationId: rawOrg && UUID_PATTERN.test(rawOrg) ? rawOrg : "",
      providerCode: searchParams.get("provider_code") ?? "",
      showSuperseded,
      showScheduled,
    }),
    [searchParams, rawOrg, rawDirection, showSuperseded, showScheduled],
  );

  const scopeOrganizationId = filters.organizationId || null;

  const activeFilterCount = useMemo(() => {
    const values = [filters.corridor, filters.direction, tab === "costs" ? filters.providerCode : ""];
    return values.filter(Boolean).length;
  }, [filters, tab]);

  const clearFilters = useCallback(
    () => patchParams({ corridor: null, direction: null, provider_code: null }),
    [patchParams],
  );

  const focusCorridor = useCallback(
    (route: ProviderRailRoute, nextTab: FeeTabId) =>
      patchParams({
        tab: nextTab,
        corridor: corridorFilterKey(route),
        direction: route.direction,
      }),
    [patchParams],
  );

  /* ------------------------------- example amounts ------------------------------- */

  const exampleAmountFor = useCallback(
    (currency: string | null | undefined) =>
      (currency && exampleAmounts[currency]) || defaultExampleAmount(currency),
    [exampleAmounts],
  );

  /* --------------------------------- derived views --------------------------------- */

  const orderedRoutes = useMemo(
    () =>
      [...routes].sort(
        (a, b) =>
          countryName(a.country).localeCompare(countryName(b.country)) ||
          formatRail(a.rail).localeCompare(formatRail(b.rail)) ||
          (a.direction === b.direction ? 0 : a.direction === PaymentDirection.PAYIN ? -1 : 1),
      ),
    [routes],
  );

  const corridors = useMemo(
    () => buildCorridorCoverage(orderedRoutes, fees, exampleAmountFor, now, scopeOrganizationId, taxRows),
    [orderedRoutes, fees, exampleAmountFor, now, scopeOrganizationId, taxRows],
  );

  const scoped = scopeOrganizationId != null;
  const visibleCorridors = useMemo(
    () => corridors.filter((corridor) => matchesFilter(corridor, corridorFilter, scoped)),
    [corridors, corridorFilter, scoped],
  );

  const corridorKpis = useMemo<CorridorKpis>(
    () => ({
      total: corridors.length,
      priced: corridors.filter((corridor) => corridor.status === "PRICED").length,
      unpriced: corridors.filter((corridor) => matchesFilter(corridor, "unpriced", scoped)).length,
      special: corridors.filter((corridor) => matchesFilter(corridor, "special", scoped)).length,
      negative: corridors.filter((corridor) => matchesFilter(corridor, "negative", scoped)).length,
      scheduled: corridors.filter((corridor) => matchesFilter(corridor, "scheduled", scoped)).length,
    }),
    [corridors, scoped],
  );

  const organizationsWithSpecialPrice = useMemo(
    () => [...new Set(corridors.flatMap((corridor) => corridor.orgOverrides.map((o) => o.organizationId)))],
    [corridors],
  );

  const allGroups = useMemo(() => groupFeesByScope(fees, now), [fees, now]);

  const groups = useMemo(() => {
    if (tab !== "commissions" && tab !== "costs") return [];
    const kind = tab === "commissions" ? PaymentFeeKind.COLOCA_COMMISSION : PaymentFeeKind.PROVIDER_COST;
    const [country, rail, currency] = filters.corridor ? filters.corridor.split("|") : [null, null, null];

    return allGroups
      .filter((group) => group.feeKind === kind)
      .filter((group) => {
        const { scope } = group;
        // A filtered dimension keeps rules that pin it AND rules that apply to any value, as resolution does.
        if (country && scope.country != null && scope.country !== country) return false;
        if (rail && scope.rail != null && scope.rail !== (rail as PaymentFeeRail)) return false;
        if (currency && scope.currency != null && scope.currency !== currency) return false;
        if (filters.direction && scope.direction != null && scope.direction !== filters.direction) {
          return false;
        }
        if (tab === "commissions" && (scope.organization_id ?? null) !== null) {
          if (scope.organization_id !== scopeOrganizationId) return false;
        }
        if (
          tab === "costs" &&
          filters.providerCode &&
          scope.provider_code != null &&
          scope.provider_code !== filters.providerCode
        ) {
          return false;
        }
        if (!showScheduled && !group.rows.some((row) => row.status !== "SCHEDULED")) return false;
        return true;
      })
      .map((group) => ({
        ...group,
        rows: group.rows.filter((row) => {
          if (!showSuperseded && (row.status === "SUPERSEDED" || row.status === "EXPIRED")) return false;
          if (!showScheduled && row.status === "SCHEDULED") return false;
          return true;
        }),
      }));
  }, [allGroups, tab, filters, scopeOrganizationId, showSuperseded, showScheduled]);

  const highlightGroup = useCallback((key: string) => {
    setExpandedKeys((current) => ({ ...current, [key]: true }));
    setHighlightedKey(key);
  }, []);

  useEffect(() => {
    if (!highlightedKey) return;
    const timer = setTimeout(() => setHighlightedKey(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedKey]);

  return {
    fees,
    now,
    isLoading,
    isRefreshing,
    hasError,
    hasAccess,
    truncated,
    loadedCount: fees.length,
    totalCount,
    refresh,
    loadAll,

    tab,
    setTab: useCallback((next: FeeTabId) => patchParams({ tab: next }), [patchParams]),

    exampleAmounts,
    exampleAmountFor,
    setExampleAmount: useCallback((currency: string, value: string) => {
      if (!EXAMPLE_AMOUNT_PATTERN.test(value)) return;
      setExampleAmounts((current) => ({ ...current, [currency]: value }));
    }, []),
    resetExampleAmount: useCallback((currency: string) => {
      setExampleAmounts((current) => {
        const next = { ...current };
        delete next[currency];
        return next;
      });
    }, []),

    filters,
    setCorridor: useCallback((value: string) => patchParams({ corridor: value }), [patchParams]),
    setDirection: useCallback(
      (value: PaymentDirection | "") => patchParams({ direction: value }),
      [patchParams],
    ),
    scopeOrganizationId,
    setScopeOrganizationId: useCallback(
      (value: string | null) => {
        setCorridorFilter("all");
        patchParams({ organization_id: value });
      },
      [patchParams],
    ),
    setProviderCode: useCallback((value: string) => patchParams({ provider_code: value }), [patchParams]),
    setShowSuperseded,
    setShowScheduled,
    clearFilters,
    hasActiveFilters: activeFilterCount > 0,
    activeFilterCount,
    focusCorridor,

    corridors,
    visibleCorridors,
    corridorFilter,
    setCorridorFilter,
    corridorKpis,
    organizationsWithSpecialPrice,

    groups,
    expandedKeys,
    toggleGroup: useCallback(
      (key: string) => setExpandedKeys((current) => ({ ...current, [key]: !current[key] })),
      [],
    ),
    highlightedKey,
    highlightGroup,
  };
}

/** Scope key of a fee row — re-exported so the page can highlight the group a new row landed in. */
export function groupKeyOf(fee: PaymentFee): string {
  return scopeKey(fee.fee_kind, feeScopeOf(fee));
}
